import * as React from "react";
import { Player } from "theoplayer/THEOplayer.chromeless";
import { cx } from "utils/join-classnames";
import { Icon } from "ui/components";
import $ from "./styles.module.scss";

type ValueTypes = "dot" | "end" | "start";

interface Props {
    startOffset?: number;
    endOffset?: number;
    progress: number;
    max: number;
    onDraggingStart?: () => void;
    onDraggingEnd?: () => void;
    onChange(valueName: ValueTypes, value: number): void;
    className?: string;
    vidRef: React.RefObject<Player | null>;
}

const DurationBarV2: React.FC<Props> = ({
    startOffset = 0,
    endOffset = 0,
    progress,
    max,
    onDraggingStart,
    onDraggingEnd,
    onChange,
    vidRef,
    className,
}) => {
    const [isDragging, setIsDragging] = React.useState<ValueTypes | null>(null);

    const barRef = React.useRef<HTMLDivElement>(null);
    const dotRef = React.useRef<HTMLDivElement>(null);
    const progressRef = React.useRef<HTMLDivElement>(null);
    const dotCaretRef = React.useRef<HTMLDivElement>(null);
    const timestampRef = React.useRef<HTMLDivElement>(null);
    const startTimeRef = React.useRef<HTMLDivElement>(null);
    const endTimeRef = React.useRef<HTMLDivElement>(null);
    const animationFrame = React.useRef<number | null>(null);
    const trimRef = React.useRef<[number, number]>([startOffset, endOffset]);
    const maxRef = React.useRef<number>(max);

    const [currentTime, setCurrentTime] = React.useState<number>(progress);
    const [, setInitialX] = React.useState<number>(0);
    const [currentX, setCurrentX] = React.useState<number>(0);

    const calculateOffsetLeft = (value?: number): number => {
        if (!value || !barRef.current) {
            return 0;
        }
        const containerWidth = barRef.current.offsetWidth;
        const calcOffset =
            (Math.round(value * 100) / Math.round(maxRef.current * 100)) *
            containerWidth;
        const leftOffset = Math.max(Math.min(calcOffset, containerWidth), 0);
        return leftOffset;
    };

    const updateVideoTime = () => {
        if (
            !vidRef.current ||
            !timestampRef.current ||
            !dotRef.current ||
            !progressRef.current ||
            !dotCaretRef.current ||
            !trimRef.current
        ) {
            return;
        }
        const [trimStartRef, trimEndRef] = trimRef.current;
        const timestamp = timestampRef.current;
        let newProgress = vidRef.current.currentTime;
        let relativeProgressOffset = calculateOffsetLeft(newProgress);
        const relativeStartOffset = calculateOffsetLeft(trimStartRef);
        const relativeEndOffset = calculateOffsetLeft(
            maxRef.current - trimEndRef,
        );
        if (relativeProgressOffset >= relativeEndOffset) {
            relativeProgressOffset = relativeEndOffset;
            vidRef.current.pause();
            newProgress = maxRef.current - (trimEndRef || 0);
        }
        const offset = `${relativeProgressOffset}px`;
        const progressWidth = `${
            relativeProgressOffset - relativeStartOffset
        }px`;

        dotRef.current.style.left = offset;
        progressRef.current.style.left = `${relativeStartOffset}px`;
        progressRef.current.style.width = progressWidth;
        dotCaretRef.current.style.left = `${relativeProgressOffset - 7}px`;
        timestamp.style.left = `${relativeProgressOffset - 50}px`;
        timestamp.innerText = `${newProgress.toFixed(2)}`;
    };

    const handleDotMouseDown =
        (valueName: ValueTypes): React.MouseEventHandler<HTMLInputElement> =>
        (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            if (!barRef.current) {
                return;
            }
            if (onDraggingStart) {
                onDraggingStart();
            }
            const { left } = barRef.current.getBoundingClientRect();
            const newInitialX = ev.clientX - left;
            setInitialX(newInitialX);
            setCurrentX(newInitialX);
            setIsDragging(valueName);
            updateVideoTime();
        };

    // Support clicking in the timeline to move the playhead
    const handleBarMouseDown = (ev: React.MouseEvent) => {
        if (!barRef.current) {
            return;
        }
        const { left } = barRef.current.getBoundingClientRect();
        const newInitialX = ev.clientX - left;
        const multiplier = Math.max(
            Math.min(newInitialX / barRef.current.offsetWidth, 1),
            0,
        );
        const value = multiplier * max;
        setCurrentTime(value);
        onChange("dot", value);
        updateVideoTime();
    };

    const handleMouseMove = React.useCallback(
        (ev: MouseEvent) => {
            if (
                !barRef.current ||
                !isDragging ||
                !vidRef.current ||
                !trimRef.current ||
                !maxRef.current
            ) {
                return;
            }

            const [trimStartRef, trimEndRef] = trimRef.current;
            const { left } = barRef.current.getBoundingClientRect();
            const newCurrentX = ev.clientX - left;
            setCurrentX(newCurrentX);
            const multiplier = Math.max(
                Math.min(newCurrentX / barRef.current.offsetWidth, 1),
                0,
            );
            let value = multiplier * max;
            if (isDragging === "start") {
                setCurrentTime(0);
            } else if (isDragging === "end") {
                value = Math.max(value, trimStartRef);
                value = max - value;
                setCurrentTime(max - value);
            } else if (isDragging === "dot") {
                if (value < trimStartRef) {
                    value = trimStartRef;
                } else if (value >= vidRef.current.duration - trimEndRef) {
                    value = vidRef.current.duration - trimEndRef;
                }
                setCurrentTime(value);
            }
            onChange(isDragging, value);
        },
        [currentX, endOffset, isDragging, max, onChange, trimRef, vidRef],
    );

    const handleMouseUp = () => {
        if (onDraggingEnd) {
            onDraggingEnd();
        }
        if (isDragging && vidRef.current) {
            if (isDragging === "start") {
                vidRef.current.currentTime = trimRef.current[0];
            } else if (isDragging === "end") {
                vidRef.current.currentTime = max - (trimRef.current[1] || 0);
            }
            setIsDragging(null);
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        }
    };

    React.useEffect(() => {
        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            updateVideoTime();
        }
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging]);

    const animate = () => {
        if (!vidRef.current?.paused) {
            updateVideoTime();
        }
        animationFrame.current = requestAnimationFrame(animate);
    };

    React.useEffect(() => {
        animationFrame.current = requestAnimationFrame(animate);
        return () => {
            if (animationFrame.current) {
                cancelAnimationFrame(animationFrame.current);
            }
        };
    }, []);

    React.useEffect(() => {
        setCurrentTime(progress);
        updateVideoTime();
    }, [progress]);

    React.useEffect(() => {
        trimRef.current = [startOffset || 0, endOffset || 0];
        updateVideoTime();
    }, [startOffset, endOffset]);

    React.useEffect(() => {
        maxRef.current = max;
        updateVideoTime();
    }, [max]);

    const relativeStartOffset = calculateOffsetLeft(startOffset);
    const relativeEndOffset = calculateOffsetLeft(max - (endOffset || 0));
    let newProgress = currentTime;
    let relativeProgressOffset = calculateOffsetLeft(currentTime);
    if (relativeProgressOffset > relativeEndOffset) {
        relativeProgressOffset = relativeEndOffset;
        newProgress = max - (endOffset || 0);
    }
    const barFillStyles: React.CSSProperties = {
        width: `${max - relativeStartOffset - (max - relativeEndOffset)}px`,
        left: `${relativeStartOffset}px`,
    };
    const rightTimestampOffset =
        barRef.current && barRef.current.offsetWidth - relativeEndOffset;
    const barDotStyles = (offset: number | undefined): React.CSSProperties => ({
        left: `${offset}px`,
    });

    return (
        <div className={cx($.bar__container, className)}>
            <div
                ref={barRef}
                className={$.bar}
                onMouseDown={handleBarMouseDown.bind(this)}
                role="presentation"
            >
                <div style={barFillStyles} className={$.bar_fill} />
                {max > 0 && (
                    <>
                        <div
                            className={$.bar__progress}
                            onMouseDown={handleBarMouseDown.bind(this)}
                            ref={progressRef}
                            role="presentation"
                        />
                        <div
                            style={barDotStyles(relativeProgressOffset)}
                            className={$.bar__dot}
                            onMouseDown={handleDotMouseDown("dot")}
                            ref={dotRef}
                            role="presentation"
                        />
                        <div
                            style={barDotStyles(relativeProgressOffset - 7)}
                            className={$.bar__dot__caret}
                            onMouseDown={handleDotMouseDown("dot")}
                            ref={dotCaretRef}
                            role="presentation"
                        >
                            <svg
                                viewBox="0 0 231 283"
                                version="1.1"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g
                                    id="Page-1"
                                    stroke="none"
                                    strokeWidth="1"
                                    fill="none"
                                    fillRule="evenodd"
                                >
                                    <polygon
                                        id="Rectangle"
                                        fill="#FFFFFF"
                                        points="0 0 231 0 231 170 0 170"
                                    />
                                    <path
                                        d="M108,282.281938 L0,170 L108,170 L108,282.281938 Z M119,170 L231,170 L119,282.476596 L119,170 Z"
                                        id="Combined-Shape"
                                        fill="#FFFFFF"
                                    />
                                    <path
                                        d="M119.5,220.5 L121,222"
                                        id="Line"
                                        fill="#FFFFFF"
                                    />
                                </g>
                            </svg>
                        </div>
                        <div
                            className={cx($.bar__trim, $.is__start)}
                            onMouseDown={handleDotMouseDown("start")}
                            style={barDotStyles(relativeStartOffset - 27)}
                            role="presentation"
                        >
                            <Icon name="mark-in" />
                        </div>

                        <div
                            style={barDotStyles(relativeEndOffset - 9)}
                            className={cx($.bar__trim, $.is__end)}
                            onMouseDown={handleDotMouseDown("end")}
                            role="presentation"
                        >
                            <Icon name="mark-out" />
                        </div>

                        <div
                            ref={timestampRef}
                            style={barDotStyles(relativeProgressOffset - 50)}
                            className={$.bar__timestamp}
                        >
                            {newProgress.toFixed(2)}
                        </div>
                        <div
                            ref={startTimeRef}
                            style={barDotStyles(relativeStartOffset - 54)}
                            className={$.bar__bottom__timestamp}
                        >
                            {startOffset.toFixed(2)}
                        </div>
                        <div
                            ref={endTimeRef}
                            style={{ right: `${rightTimestampOffset! - 56}px` }}
                            className={$.bar__bottom__timestamp}
                        >
                            {(max - (endOffset || 0)).toFixed(2)}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DurationBarV2;
